import { ISessionsStoreState, filterActiveSessions } from "@gtmhub/sessions";
import { INgRedux } from "@gtmhub/state-management";
import { ITeam, ITeamsStoreState } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { unique } from "@gtmhub/util";
import { ArrayFilterType, IDynamicValue } from "./models";

export const getTeamPlusMemberIds = (teams: Record<string, ITeam>, teamId: string): string[] => {
  const ids = [teamId];

  const team = teams[teamId];
  if (!team) {
    return ids;
  }

  if (team.members) {
    ids.push(...team.members);
  }
  if (team.manager) {
    ids.push(team.manager);
  }
  return ids;
};

const getTeamOrganizationIds = (teams: Record<string, ITeam>, teamId: string): string[] => {
  const ids = getTeamPlusMemberIds(teams, teamId);
  const children = Object.values(teams).filter((t) => t.parentId === teamId);
  for (const child of children) {
    ids.push(...getTeamOrganizationIds(teams, child.id));
  }
  return ids;
};

const getTeamPlusSubteamsIds = (teams: Record<string, ITeam>, teamId: string): string[] => {
  const ids = [teamId];
  const children = Object.values(teams).filter((t) => t.parentId === teamId);
  for (const child of children) {
    ids.push(...getTeamPlusSubteamsIds(teams, child.id));
  }

  return ids;
};

const getUserTeamIds = (teams: Record<string, ITeam>, userId: string): string[] =>
  Object.values(teams)
    .filter((t) => t.manager === userId || (t.members && t.members.includes(userId)))
    .map((t) => t.id);

export class GridFiltersDynamicValueFactory {
  static $inject = ["$ngRedux"];

  constructor(private $ngRedux: INgRedux) {}

  evaluate(dynamicValue: IDynamicValue, arrayFilterType: ArrayFilterType): string[] {
    switch (dynamicValue.type) {
      case "activeSessions":
        return filterActiveSessions(this.$ngRedux.getState<ISessionsStoreState>().sessions.items).map((session) => session.id);

      case "me":
        return [getCurrentUserId()];

      case "myTeams":
        return this.getMyTeamIds();

      case "teamPlusMembers":
        return this.getTeamPlusMemberIds(dynamicValue.teamId);

      case "teamOrg":
      case "teamOrgPlusMembers":
        return this.getTeamOrganizationIds(dynamicValue.teamId, arrayFilterType);

      default:
        throw new Error(`Unknown dynamic filter value: ${dynamicValue}`);
    }
  }

  private getMyTeamIds(): string[] {
    const currentUserId = getCurrentUserId();
    const teams = this.$ngRedux.getState<ITeamsStoreState>().teams.map;
    return unique(getUserTeamIds(teams, currentUserId));
  }

  private getTeamPlusMemberIds(teamId: string): string[] {
    const teams = this.$ngRedux.getState<ITeamsStoreState>().teams.map;
    return unique(getTeamPlusMemberIds(teams, teamId));
  }

  private getTeamOrganizationIds(teamId: string, arrayFilterType: ArrayFilterType): string[] {
    const teams = this.$ngRedux.getState<ITeamsStoreState>().teams.map;
    if (arrayFilterType === "assignee") {
      return unique(getTeamOrganizationIds(teams, teamId));
    } else if (arrayFilterType === "team") {
      return unique(getTeamPlusSubteamsIds(teams, teamId));
    } else {
      throw new Error(`Cannot filter on team plus organisation when the filter type is ${arrayFilterType}`);
    }
  }
}
